/** Commenting on a living document as in Google Docs: select any text and comment on
 * it. Each comment is sent as it is written, into the review conversation the agents
 * answer in the background; text with a comment still open stays highlighted, and
 * clicking it opens the comment. See docs/Y2026-Q4/design-review-mode.md section 3. */
import type { ReviewPhase } from "../api/hooks/useDashboard";
import {
  type MouseEvent as ReactMouseEvent,
  type ReactNode,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { createPortal } from "react-dom";

import { useKindeAuth } from "@kinde-oss/kinde-auth-react";

import {
  type ReviewAnchor,
  type ReviewComment,
  type ReviewLevel,
  type ReviewTarget,
  useSubmitReview,
} from "../api/hooks/useReview";
import {
  anchorLabel,
  REVIEW_CHROME,
  REVIEW_PORTAL,
  ReviewContext,
  type ReviewState,
} from "../utils/reviewState";
import { useSentNotice } from "../state/sentNotice";
import { conversationHref } from "../utils/reviewRunHref";
import { sendOnEnter } from "../utils/sendShortcut";
import { findQuote, pointAt, quoteOf } from "../utils/textAnchors";
import { ReviewCommentItem } from "./ReviewBar";
import type { OnReply } from "./RunItemCards";
import styles from "./Review.module.css";

/** The project a document key (``project:<id>``) names. */
const targetOf = (docKey: string): ReviewTarget => ({
  target: "project",
  project_id: docKey.replace(/^project:/, ""),
});

// Room the selection button needs between where it starts and the window's right edge,
// for the wider of its labels ("Comment").
const SELECTION_BUTTON_ROOM = 140;

/** The box of the point the selection ends at (where the pointer let go, the start of a
 * backwards drag), so the Comment button sits just after it. Where that point is an
 * element boundary rather than a place in text (a triple-click ends at the next block),
 * the last line of the selection stands in. */
const selectionEndRect = (selection: Selection, range: Range): DOMRect => {
  const focus = selection.focusNode;
  if (focus && focus.nodeType === Node.TEXT_NODE) {
    const caret = document.createRange();
    caret.setStart(focus, selection.focusOffset);
    const rect = caret.getBoundingClientRect();
    if (rect.height > 0) return rect;
  }
  const lines = [...range.getClientRects()].filter((r) => r.width > 0);
  return lines.length > 0
    ? lines[lines.length - 1]
    : range.getBoundingClientRect();
};

/** The document and anchor a selection points at, or null outside any content card.
 * ``docKey`` is null for a card that belongs to no project's document (a reply in a
 * conversation about no single project): it can be quoted, not commented on. */
const anchorOf = (
  range: Range,
  surface: HTMLElement,
  defaultDoc: string | null,
  defaultLevel: ReviewLevel,
): { docKey: string | null; anchor: ReviewAnchor } | null => {
  const node = range.commonAncestorContainer;
  const element = node instanceof Element ? node : node.parentElement;
  if (!element || element.closest(REVIEW_CHROME)) return null;
  const docRoot = element.closest<HTMLElement>("[data-review-doc]");
  const docKey = docRoot?.dataset.reviewDoc ?? defaultDoc;
  // Only the content cards take comments, never the page's own chrome around them.
  const block = element.closest<HTMLElement>("[data-review-level]");
  if (!block) return null;
  const { quote, prefix, suffix } = quoteOf(range, docRoot ?? surface);
  if (!quote) return null;
  // What the operator was looking at, so the agent applying it sees the same thing.
  const shown = block.innerText.replace(/\s+/g, " ").trim();
  return {
    docKey,
    anchor: {
      level:
        (block.dataset.reviewLevel as ReviewLevel | undefined) ?? defaultLevel,
      block: block.dataset.reviewBlock ?? "",
      quote: quote.slice(0, 2000),
      prefix,
      suffix,
      rendered: shown.slice(0, 3000),
      item_id: block.dataset.reviewItem ?? "",
      agent: block.dataset.reviewAgent ?? "",
    },
  };
};

/** A selection as a Markdown quote, shaped like a card's Reply quote (RunItemCards). */
const quoteForReply = (text: string): string => `> ${text}\n\n`;

// Space kept between the composer and the window's edge.
const COMPOSER_MARGIN = 8;

// On a touch screen the Comment button and the composer dock at the bottom of the
// viewport: iOS draws its Copy / Look Up menu above or below the selection as space
// allows, and a page cannot suppress it, so any spot beside the selection can end up
// underneath it.
const isTouch = (): boolean =>
  typeof window !== "undefined" &&
  window.matchMedia("(pointer: coarse)").matches;

/** Where in the window the button a composer opens from is, or null to dock it. The
 * composer stays there while the page scrolls: a selection in the docked conversation
 * pane does not move with the page. */
const composerAt = (
  from: Element | undefined,
): { top: number; left: number } | null => {
  if (!from || isTouch()) return null;
  const rect = from.getBoundingClientRect();
  return { top: rect.top, left: rect.left };
};

/** ``at`` is where in the window the button it opened from is: the text field's top
 * lines up with the button's, so the eye is already where the typing goes, and the box's
 * left edge with the button's; each moves only as far as keeps the box on screen.
 * Without one (a touch screen) it docks in the corner. */
const CommentComposer = ({
  anchor,
  initialBody,
  at,
  separate,
  sending,
  error,
  onSave,
  onCancel,
}: {
  anchor: ReviewAnchor;
  initialBody: string;
  at: { top: number; left: number } | null;
  /** Opened from a conversation, where the comment does not land in the chat read. */
  separate: boolean;
  sending: boolean;
  error: string | null;
  onSave: (body: string) => void;
  onCancel: () => void;
}) => {
  const [body, setBody] = useState(initialBody);
  const box = useRef<HTMLDivElement>(null);
  const field = useRef<HTMLTextAreaElement>(null);
  const [left, setLeft] = useState(at?.left ?? 0);
  const [top, setTop] = useState(at?.top ?? 0);
  useLayoutEffect(() => {
    if (!at || !box.current || !field.current) return;
    const right = document.documentElement.clientWidth - COMPOSER_MARGIN;
    const bottom = window.innerHeight - COMPOSER_MARGIN;
    setLeft(
      Math.max(COMPOSER_MARGIN, Math.min(at.left, right - box.current.offsetWidth)),
    );
    setTop(
      Math.max(
        COMPOSER_MARGIN,
        Math.min(at.top - field.current.offsetTop, bottom - box.current.offsetHeight),
      ),
    );
  }, [at]);
  // Focused once placed, with the caret after any text already in it.
  useEffect(() => {
    const el = field.current;
    if (!el) return;
    el.focus({ preventScroll: true });
    el.setSelectionRange(el.value.length, el.value.length);
  }, []);
  const submit = () => {
    if (body.trim() && !sending) onSave(body.trim());
  };
  return (
    <div
      ref={box}
      className={at ? styles.composerAt : styles.composer}
      style={at ? { top, left } : undefined}
      role="dialog"
      aria-label="Add a comment"
      data-review-chrome
    >
      <p className={styles.composerWhere}>{anchorLabel(anchor)}</p>
      {separate && (
        <p className={styles.composerSeparate}>
          This goes to a side chat about this site, not to this conversation. To ask here,
          use Reply.
        </p>
      )}
      {anchor.quote && (
        <blockquote className={styles.quote}>{anchor.quote}</blockquote>
      )}
      <textarea
        ref={field}
        className={styles.textarea}
        value={body}
        onChange={(e) => setBody(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Escape") onCancel();
          else sendOnEnter(submit)(e);
        }}
      />
      {error && <p className={styles.error}>{error}</p>}
      <div className={styles.actions}>
        <span className={styles.shortcutHint}>
          Enter to send, Shift+Enter for a new line
        </span>
        <button type="button" className={styles.secondary} onClick={onCancel}>
          Cancel
        </button>
        <button
          type="button"
          className={styles.primary}
          disabled={!body.trim() || sending}
          onClick={submit}
        >
          {sending ? "Sending…" : "Comment"}
        </button>
      </div>
    </div>
  );
};

type Marked = { comment: ReviewComment; range: Range };

// Must agree with the ::highlight() rule in global.css.
const HIGHLIGHT_COMMENT = "review-comment";

const supportsHighlights = (): boolean =>
  typeof CSS !== "undefined" &&
  "highlights" in CSS &&
  typeof Highlight !== "undefined";

/** ``defaultDoc`` is the document a page is (null on one holding several, like the
 * dashboard, where each part carries ``data-review-doc``). ``comments`` are submitted
 * ones on ``defaultDoc`` to keep highlighted. */
export const ReviewProvider = ({
  available,
  defaultDoc,
  defaultLevel,
  comments,
  phases = {},
  briefingVersion = null,
  projectId,
  onQuote,
  children,
}: {
  available: boolean;
  defaultDoc: string | null;
  defaultLevel: ReviewLevel;
  /** Takes a selection in a card with no document: it is quoted into the page's reply
   * box rather than commented on. Null where there is no reply box. */
  onQuote: OnReply;
  comments: ReviewComment[];
  /** What is happening to each comment in flight. */
  phases?: Record<string, ReviewPhase>;
  /** The briefing version the page shows, recorded with each comment made on it. */
  briefingVersion?: number | null;
  projectId: string | null;
  children: ReactNode;
}) => {
  const { user } = useKindeAuth();
  const submit = useSubmitReview();
  const showSent = useSentNotice();
  const [pending, setPending] = useState<{
    docKey: string;
    anchor: ReviewAnchor;
    body: string;
    at: { top: number; left: number } | null;
    separate: boolean;
  } | null>(null);
  const [selected, setSelected] = useState<{
    /** Null: the selection is quoted into the reply box, not commented on. */
    docKey: string | null;
    anchor: ReviewAnchor;
    top: number;
    left: number;
  } | null>(null);
  const [opened, setOpened] = useState<{
    item: Marked;
    top: number;
    left: number;
  } | null>(null);
  const surface = useRef<HTMLDivElement>(null);
  const marked = useRef<Marked[]>([]);
  const choice = useRef<HTMLDivElement>(null);
  // Whether the surface is on screen: a minimised pane, or a tab not shown, keeps its
  // composer (and what is typed in it) but hides it with the surface.
  const [shown, setShown] = useState(true);
  useEffect(() => {
    const root = surface.current;
    if (!root) return;
    const watch = new ResizeObserver(() => setShown(root.getClientRects().length > 0));
    watch.observe(root);
    return () => watch.disconnect();
  }, []);
  const portalId = useId();

  // A selection anywhere in a document offers a comment on exactly that text. With a
  // mouse it is read when the button is released, so nothing appears mid-drag. A touch
  // selection (long-press and drag handles on iOS) fires no mouseup, so there it is read
  // on ``selectionchange``, settled briefly so it offers once the drag stops.
  const pressing = useRef(false);
  // Read through a ref: a page passes a fresh function each render, and re-subscribing
  // the listeners would drop a read already scheduled for the release.
  const quote = useRef(onQuote);
  quote.current = onQuote;
  useEffect(() => {
    if (!available) return;
    let timer: number | undefined;
    const read = () => {
      const selection = window.getSelection();
      const root = surface.current;
      if (
        !selection ||
        selection.isCollapsed ||
        !root ||
        selection.rangeCount === 0 ||
        // A minimised pane, or a tab not shown: nothing on it is in front of the operator.
        root.getClientRects().length === 0
      ) {
        // Tapping the button collapses the selection on touch screens before the tap
        // lands, so it must not take the button away mid-press.
        if (!pressing.current) setSelected(null);
        return;
      }
      const range = selection.getRangeAt(0);
      const node = range.commonAncestorContainer;
      // On the page, or in a popover opened from it (InfoToggle, the heatmap's cards).
      const inPortal =
        (node instanceof Element ? node : node.parentElement)
          ?.closest(`[${REVIEW_PORTAL}]`)
          ?.getAttribute(REVIEW_PORTAL) === portalId;
      const found =
        root.contains(node) || inPortal
          ? anchorOf(range, root, defaultDoc, defaultLevel)
          : null;
      if (!found || (!found.docKey && !quote.current)) {
        setSelected(null);
        return;
      }
      const end = selectionEndRect(selection, range);
      setSelected({
        ...found,
        // In the window, centred on the line by the button's translateY(-50%).
        top: end.top + end.height / 2,
        left: Math.max(8, Math.min(end.right + 6, window.innerWidth - SELECTION_BUTTON_ROOM)),
      });
    };
    const touch = window.matchMedia("(pointer: coarse)").matches;
    const onChange = () => {
      window.clearTimeout(timer);
      if (touch) {
        timer = window.setTimeout(read, 300);
        return;
      }
      // With a mouse, a change only ever takes the button away (a click elsewhere).
      if (window.getSelection()?.isCollapsed) setSelected(null);
    };
    // After the browser has settled the selection the release ends.
    const onRelease = () => {
      window.clearTimeout(timer);
      timer = window.setTimeout(read, 0);
    };
    // The buttons sit in the window: a scroll moves the selection under them, so they
    // follow it (or go, once it has left the surface).
    let frame = 0;
    const onScroll = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        if (!window.getSelection()?.isCollapsed) read();
      });
    };
    document.addEventListener("selectionchange", onChange);
    document.addEventListener("scroll", onScroll, true);
    if (!touch) {
      document.addEventListener("mouseup", onRelease);
      // Shift+arrow selections.
      document.addEventListener("keyup", onRelease);
    }
    return () => {
      document.removeEventListener("selectionchange", onChange);
      document.removeEventListener("scroll", onScroll, true);
      cancelAnimationFrame(frame);
      document.removeEventListener("mouseup", onRelease);
      document.removeEventListener("keyup", onRelease);
      window.clearTimeout(timer);
    };
  }, [available, defaultDoc, defaultLevel, portalId]);

  // The choice is kept inside the window, by its own width: with both buttons it is wider
  // than the room left for one, and a button half off screen scrolls the page when pressed.
  useLayoutEffect(() => {
    const box = choice.current;
    if (!box || !selected || isTouch()) return;
    const over =
      box.getBoundingClientRect().right -
      (document.documentElement.clientWidth - COMPOSER_MARGIN);
    if (over > 0) box.style.left = `${Math.max(COMPOSER_MARGIN, selected.left - over)}px`;
  }, [selected]);

  // Every anchored quote is found again whenever the page or the comments change, and
  // painted with the CSS Highlight API, which leaves React's DOM untouched.
  useEffect(() => {
    const root = surface.current;
    // With nothing to mark it leaves the highlight alone: it is one per page, and a
    // conversation in the docked pane must not clear the comments of the page behind it.
    if (!root || !defaultDoc || comments.length === 0) {
      marked.current = [];
      return;
    }
    let timer: number | undefined;
    // A dashboard row is two table rows when split, each marked with its document.
    const docRoots = (docKey: string): Node[] => {
      const parts = [
        ...root.querySelectorAll(`[data-review-doc="${CSS.escape(docKey)}"]`),
      ];
      return parts.length > 0 ? parts : [root];
    };
    const find = (
      docKey: string,
      quote: string,
      prefix: string,
      suffix: string,
    ) => {
      for (const part of docRoots(docKey)) {
        const range = findQuote(part, quote, prefix, suffix);
        if (range) return range;
      }
      return null;
    };
    const locate = () => {
      const next: Marked[] = [];
      for (const comment of comments) {
        if (
          !["open", "needs_input"].includes(comment.status) ||
          !comment.anchor.quote
        )
          continue;
        const { quote, prefix = "", suffix = "" } = comment.anchor;
        const range = find(defaultDoc, quote, prefix, suffix);
        if (range) next.push({ comment, range });
      }
      marked.current = next;
      if (supportsHighlights()) {
        CSS.highlights.set(
          HIGHLIGHT_COMMENT,
          new Highlight(...next.map((m) => m.range)),
        );
      }
    };
    locate();
    const observer = new MutationObserver(() => {
      window.clearTimeout(timer);
      timer = window.setTimeout(locate, 250);
    });
    observer.observe(root, {
      childList: true,
      subtree: true,
      characterData: true,
    });
    return () => {
      observer.disconnect();
      window.clearTimeout(timer);
      if (supportsHighlights()) CSS.highlights.delete(HIGHLIGHT_COMMENT);
    };
  }, [comments, defaultDoc]);

  // Clicking highlighted text opens the comment it carries.
  const onClick = (event: ReactMouseEvent) => {
    if (!window.getSelection()?.isCollapsed) return;
    const point = pointAt(event.clientX, event.clientY);
    const hit = point
      ? marked.current.find((m) => {
          try {
            return m.range.isPointInRange(point.node, point.offset);
          } catch {
            return false;
          }
        })
      : undefined;
    setOpened(
      hit
        ? {
            item: hit,
            top: event.clientY + window.scrollY + 12,
            left:
              Math.min(event.clientX, window.innerWidth - 380) + window.scrollX,
          }
        : null,
    );
  };

  const value = useMemo<ReviewState>(
    () => ({
      available,
      defaultDoc,
      portalId,
      startComment: (docKey, anchor, body = "", from) => {
        setSelected(null);
        setPending({ docKey, anchor, body, at: composerAt(from), separate: false });
      },
    }),
    [available, defaultDoc, portalId],
  );

  const send = (docKey: string, anchor: ReviewAnchor, body: string) => {
    const target = targetOf(docKey);
    submit.mutate(
      {
        ...target,
        comments: [{ anchor, body }],
        briefing_version: briefingVersion,
        actor_email: user?.email ?? "",
      },
      {
        onSuccess: (res) => {
          setPending(null);
          window.getSelection()?.removeAllRanges();
          showSent({
            what: "comment",
            status: res.status === "queued" ? "queued" : "started",
            href: conversationHref(
              res.conversation_id,
              target.target === "project" ? target.project_id : null,
            ),
          });
        },
      },
    );
  };

  const docked = isTouch();
  const item = opened?.item;
  return (
    <ReviewContext.Provider value={value}>
      <div ref={surface} onClick={onClick}>
        {children}
      </div>
      {/* In the body, where the page positions they are placed at hold: inside the
          docked conversation pane they would be placed against the pane. */}
      {createPortal(
        <div hidden={!shown}>
      {selected && (
        <div
          ref={choice}
          className={docked ? styles.selectionChoiceDocked : styles.selectionChoice}
          style={docked ? undefined : { top: selected.top, left: selected.left }}
          data-review-chrome
          onPointerDown={() => {
            pressing.current = true;
          }}
          onPointerCancel={() => {
            pressing.current = false;
          }}
        >
          {/* In a conversation a selection is quoted into the reply box; on a document it
              is commented on; a reply on a site's conversation takes both. */}
          {onQuote && (
            <button
              type="button"
              className={styles.selectionChoiceButton}
              onClick={() => {
                pressing.current = false;
                onQuote(quoteForReply(selected.anchor.quote));
                window.getSelection()?.removeAllRanges();
                setSelected(null);
              }}
            >
              Reply
            </button>
          )}
          {selected.docKey && (
            <button
              type="button"
              className={styles.selectionChoiceButton}
              title={onQuote ? "Goes to a side chat about this site, not to this conversation" : undefined}
              onClick={(e) => {
                pressing.current = false;
                setPending({
                  docKey: selected.docKey ?? "",
                  anchor: selected.anchor,
                  body: "",
                  at: composerAt(e.currentTarget),
                  separate: !!onQuote,
                });
                setSelected(null);
              }}
            >
              {onQuote ? "Comment in side chat" : "Comment"}
            </button>
          )}
        </div>
      )}
      {opened && item && (
        <div
          className={styles.popover}
          style={{ top: opened.top, left: opened.left }}
          data-review-chrome
        >
          <ul className={styles.threadList}>
            <ReviewCommentItem
              comment={item.comment}
              projectId={projectId}
              phase={phases[item.comment.comment_id]}
            />
          </ul>
          <div className={styles.actions}>
            <button
              type="button"
              className={styles.secondary}
              onClick={() => setOpened(null)}
            >
              Close
            </button>
          </div>
        </div>
      )}
      {pending && (
        <CommentComposer
          anchor={pending.anchor}
          initialBody={pending.body}
          at={pending.at}
          separate={pending.separate}
          onCancel={() => {
            submit.reset();
            setPending(null);
            window.getSelection()?.removeAllRanges();
          }}
          sending={submit.isLoading}
          error={
            submit.error
              ? submit.error.message || "Could not send the comment."
              : null
          }
          onSave={(body) => send(pending.docKey, pending.anchor, body)}
        />
      )}
        </div>,
        document.body,
      )}
    </ReviewContext.Provider>
  );
};
